///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch } from "react-redux";

import {
  getLaneMatchCountForTLLanes,
  applyServerPagedPricingFiltersAndRefetch,
  sortRxoTlPricingLanesAndRefetch
} from "../../../slices/bidPricingSlice";
import Arrow from "../../icons/Arrow";
import { order, division, controls } from "../../../helpers/types";
import FilterMenu from "../../form-controls/FilterMenu";
import { getGridId } from "../../../helpers/tableSelection";
import tlColumnConstants from "../../../helpers/tlColumnConstants";

const { LANE_HAS_MATCHES } = tlColumnConstants;

const SortIcon = ({ right, sortOrder }) => (
  <Arrow
    height="12px"
    width="12px"
    rotateDegree={sortOrder === order.ascending ? 180 : 0}
    className={`sort-icon ${right ? "mr-4" : "ml-4"}`}
  />
);
export const Filter = ({ handleFilter, filterOptions, ...rest }) => {
  return (
    <FilterMenu
      id="pricing-grid-filter-menu"
      disabled={false}
      options={filterOptions || []}
      handleFilter={handleFilter}
      appendTo={document.body}
      offset={[0, 8]}
      showApplyButton={true}
      isSearchable={true}
      {...rest}
    />
  );
};
const dropIndicatorClassName = "drop-indicator";

const handleDragStart = e => e.dataTransfer.setData("pick", e.target.id);

const removeDropIndicator = () => {
  while (document.getElementsByClassName(dropIndicatorClassName).length) {
    document
      .getElementsByClassName(dropIndicatorClassName)[0]
      .classList.remove(dropIndicatorClassName);
  }
};

const onDragOver = (e, field, isLaneMatch) => {
  e.preventDefault();
  if (field && !isLaneMatch) {
    removeDropIndicator();
    document
      .querySelector(`#${getGridId(isLaneMatch)} #${field}`)
      .classList.add(dropIndicatorClassName);
  }
};

export const PricingGridHeader = ({
  columns,
  role,
  columnOrder,
  adjustTopScroll,
  isLaneMatch = false,
  setColumnOrder,
  toggleColumnFilterMenu,
  filterPricingLanes,
  setColumnWidth,
  sortPricingLanes,
  sortField,
  sortOrder,
  lanesLoading,
  columnFilterMenu,
  columnFilters,
  rangeFilters,
  columnWidths,
  analyticsGridView,
  bidDivision,
  buttonLabelValue,
  bidId,
  useRxoTlServerPagedLanes
}) => {
  const dispatch = useDispatch();

  const HeaderCell = ({
    right,
    left,
    center,
    field,
    header,
    width,
    filter,
    isDraggable,
    unitLeft,
    cellUnit,
    rangeFilter,
    resize,
    alternateHeader,
    filterField,
    control,
    buttonHeader,
    buttonClick,
    sortable = true
  }) => {
    const columnFilterOptions = columnFilters[filterField ?? field];
    const columnWidth = columnWidths[field];
    const rangeFilterData = rangeFilters[field];

    const handleDrop = (e, drop) => {
      const pick = e.dataTransfer.getData("pick");
      if (drop && columnOrder.some(c => c === pick)) {
        e.stopPropagation();
        dispatch(setColumnOrder({ pick, drop, role, isLaneMatch }));
      } else {
        removeDropIndicator();
      }
    };

    const handleMouseUp = e => {
      adjustTopScroll && adjustTopScroll();
      dispatch(
        setColumnWidth({
          width: e.target.offsetWidth,
          field,
          isLaneMatch,
          analyticsGridView
        })
      );
    };

    return (
      <th
        onDragEnd={() => removeDropIndicator()}
        draggable={isDraggable === false || isLaneMatch ? false : true}
        onDragStart={handleDragStart}
        onDrop={e => handleDrop(e, isDraggable === false ? false : field)}
        onDragOver={e => onDragOver(e, field, isLaneMatch)}
        id={field}
        className={`header-cell ${width} ${right ? "right" : ""} ${
          left ? "left" : ""
        } ${center ? "center" : ""} ${
          sortField === field && sortable ? "sorted" : ""
        } ${filter || rangeFilter ? "filter" : ""} ${
          (columnFilterMenu.field === field && columnFilterMenu.visible) ||
          (columnFilterOptions && columnFilterOptions.some(f => f.selected)) ||
          rangeFilterData
            ? "filtered"
            : ""
        }`}
        data-left-unit={unitLeft ? cellUnit : ""}
        data-right-unit={unitLeft ? "" : cellUnit}
      >
        <div className="header-div">
          <div
            className={header || resize ? "resizable" : "h-32"}
            onMouseUp={handleMouseUp}
            style={{ width: columnWidth }}
          >
            {right && sortField === field && sortable && (
              <SortIcon
                right={right}
                sortOrder={sortOrder}
                onDrop={e => handleDrop(e, field)}
              />
            )}
            <span
              onDrop={e => handleDrop(e, field)}
              className={sortable ? "pointer" : ""}
              onClick={
                sortable
                  ? () => {
                      if (lanesLoading) {
                        return;
                      }
                      if (
                        useRxoTlServerPagedLanes &&
                        field !== LANE_HAS_MATCHES
                      ) {
                        dispatch(
                          sortRxoTlPricingLanesAndRefetch({
                            bidId,
                            role,
                            field
                          })
                        );
                      } else {
                        dispatch(
                          sortPricingLanes({ field, analyticsGridView })
                        );
                        isLaneMatch ||
                          dispatch(getLaneMatchCountForTLLanes());
                      }
                    }
                  : undefined
              }
              onMouseUp={e => e.stopPropagation()}
            >
              {(isLaneMatch || bidDivision === division.eu) && alternateHeader
                ? alternateHeader
                : header}
            </span>
            {control === controls.button && buttonHeader ? (
              <button
                className="button-cell cursor-pointer"
                onMouseUp={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  buttonClick &&
                    dispatch(
                      buttonClick({
                        analyticsGridView: analyticsGridView
                      })
                    );
                }}
              >
                {buttonHeader(buttonLabelValue)}
              </button>
            ) : null}
            {!right && sortField === field && sortable && (
              <SortIcon
                onDrop={e => handleDrop(e, field)}
                right={right}
                sortOrder={sortOrder}
              />
            )}
            {(filter || rangeFilter) && (
              <Filter
                field={field}
                filterField={filterField}
                filterOptions={columnFilterOptions}
                handleFilter={(items, field) => {
                  if (
                    useRxoTlServerPagedLanes &&
                    field !== LANE_HAS_MATCHES
                  ) {
                    dispatch(
                      applyServerPagedPricingFiltersAndRefetch({
                        bidId,
                        role,
                        items,
                        field
                      })
                    );
                  } else {
                    dispatch(
                      filterPricingLanes({
                        items,
                        field,
                        role,
                        isLaneMatch,
                        analyticsGridView
                      })
                    );
                    isLaneMatch || dispatch(getLaneMatchCountForTLLanes());
                  }
                }}
                show={() =>
                  dispatch(
                    toggleColumnFilterMenu({
                      field,
                      visible: true,
                      role,
                      filter,
                      isLaneMatch,
                      analyticsGridView,
                      filterField
                    })
                  )
                }
                visible={
                  columnFilterMenu.field === field && columnFilterMenu.visible
                }
                onClickOutside={() =>
                  dispatch(
                    toggleColumnFilterMenu({
                      field,
                      visible: false,
                      role,
                      filter,
                      isLaneMatch,
                      analyticsGridView,
                      filterField
                    })
                  )
                }
                rangeFilter={rangeFilter}
                handleRangeFilter={(range, field) => {
                  if (useRxoTlServerPagedLanes) {
                    dispatch(
                      applyServerPagedPricingFiltersAndRefetch({
                        bidId,
                        role,
                        range,
                        field
                      })
                    );
                  } else {
                    dispatch(
                      filterPricingLanes({
                        field,
                        role,
                        range,
                        isLaneMatch,
                        analyticsGridView
                      })
                    );
                    isLaneMatch || dispatch(getLaneMatchCountForTLLanes());
                  }
                }}
                isLaneMatch={isLaneMatch}
              />
            )}
          </div>
        </div>
      </th>
    );
  };

  return (
    <thead>
      <tr className="row">
        {columnOrder.map((c, index) => (
          <HeaderCell
            key={index}
            {...columns.find(({ field }) => field === c)}
          />
        ))}
      </tr>
    </thead>
  );
};

export default PricingGridHeader;
